<template>
    <BaseCard ref="formElement" padding="sm" class="flex flex-col gap-2">
        <BaseInput
            v-model="title"
            :aria-label="titleAriaLabel"
            :placeholder="t('journeys.goalActionForm.titlePlaceholder')"
            :disabled="isSubmitting"
            @keydown.enter="handleTitleEnter"
            @keydown.esc="handleEscape" />
        <BaseTextarea
            v-model:input="description"
            :aria-label="descriptionAriaLabel"
            :placeholder="t('journeys.goalActionForm.descriptionPlaceholder')"
            :disabled="isSubmitting"
            class="resize-none rounded-md border border-control-border bg-control-bg p-2.5 text-control-text placeholder:text-control-placeholder"
            :class="tonalThemeEnabled ? 'focus-field' : 'focus-ring'"
            @keydown.esc="handleEscape" />
        <div class="flex items-center justify-end gap-2 pt-1">
            <BaseButton theme="neutral" size="sm" :disabled="isSubmitting" @click="cancel">{{ t("common.cancel") }}</BaseButton>
            <BaseButton theme="primary" size="sm" :disabled="!title.trim() || isSubmitting" @click="handleSubmit">
                {{ isSubmitting ? t("common.saving") : t("common.save") }}
            </BaseButton>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { unlessComposing } from "~vue/utils/keyboard";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    initialTitle?: string;
    initialDescription?: string;
    isSubmitting?: boolean;
    titleAriaLabel?: string;
    descriptionAriaLabel?: string;
}>();

const emit = defineEmits<{
    submit: [title: string, description: string];
    cancel: [];
}>();

const { t } = useI18n();

function cleanText(value: string): string {
    return value.replace(/\s+/g, " ").trim();
}

const title = ref(props.initialTitle ?? "");
const description = ref(props.initialDescription ? cleanText(props.initialDescription) : "");
const formElement = ref<InstanceType<typeof BaseCard> | null>(null);

watch(
    () => props.initialTitle,
    (val) => {
        title.value = val ?? "";
    },
);
watch(
    () => props.initialDescription,
    (val) => {
        description.value = val ? cleanText(val) : "";
    },
);

function handleSubmit() {
    if (!title.value.trim() || props.isSubmitting) {
        return;
    }
    emit("submit", cleanText(title.value), cleanText(description.value));
}

function cancel() {
    if (!props.isSubmitting) {
        emit("cancel");
    }
}

const handleTitleEnter = unlessComposing(handleSubmit);
const handleEscape = unlessComposing(cancel);

async function focus() {
    await nextTick();
    const element = formElement.value?.$el;
    if (element instanceof HTMLElement) {
        element.querySelector("input")?.focus();
    }
}

function reset() {
    title.value = "";
    description.value = "";
}

defineExpose({ focus, reset });
</script>
